import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileText, 
  AlertTriangle, 
  Clock, 
  User,
  ExternalLink} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useSharedData } from '@/hooks/useSharedData';

interface AgreementNotification {
  _id: string;
  employee: {
    _id: string;
    user: {
      firstName: string;
      lastName: string;
      employeeId: string;
    };
  };
  agreementInfo: {
    startDate: string;
    endDate: string;
    periodInMonths: string;
    notifyPeriodInDays: string;
    note: string;
  }[];
}

const AgreementNotifications: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const { employees, loading: sharedLoading } = useSharedData();
  const [notifications, setNotifications] = useState<AgreementNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); // Show 6 notifications per page (3 columns × 2 rows)

  // Check if user has permission to view agreement notifications
  const canViewNotifications = hasPermission('EMPLOYEES_VIEW') || hasPermission('EMPLOYEES_EDIT');

  useEffect(() => {
    if (canViewNotifications && employees && employees.length > 0) {
      processAgreementNotifications();
    }
  }, [canViewNotifications, employees]);

  const processAgreementNotifications = () => {
    try {
      setLoading(true);
      const agreementsNeedingAttention: AgreementNotification[] = [];

      employees.forEach(employee => {
        if (employee.agreementInfo && employee.agreementInfo.length > 0) {
          // Sort agreements by start date (newest first)
          const sortedAgreements = employee.agreementInfo.sort((a: any, b: any) => {
            const dateA = new Date(a.startDate || 0);
            const dateB = new Date(b.startDate || 0);
            return dateB.getTime() - dateA.getTime();
          });

          // Get the most recent agreement
          const mostRecentAgreement = sortedAgreements[0];
          
          // Only show notification if the most recent agreement is expiring/expired
          if (mostRecentAgreement && mostRecentAgreement.endDate) {
            const daysUntilExpiry = getDaysUntilExpiry(mostRecentAgreement.endDate);
            const notifyDays = parseInt(mostRecentAgreement.notifyPeriodInDays || '0', 10);
            
            // Include only if the agreement is within the notification period or already expired
            if (daysUntilExpiry <= notifyDays) {
              agreementsNeedingAttention.push({
                _id: employee._id,
                employee: {
                  _id: employee._id,
                  user: {
                    firstName: employee.user?.firstName || '',
                    lastName: employee.user?.lastName || '',
                    employeeId: employee.user?.employeeId || ''
                  }
                },
                agreementInfo: [mostRecentAgreement] // Only show the most recent agreement
              });
            }
          }
        }
      });

      setNotifications(agreementsNeedingAttention);
      setCurrentPage(1); // Reset to first page when data changes
    } catch (err) {
      console.error('Error processing agreement notifications:', err);
      setError('Failed to process agreement notifications');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate days until expiry
  const getDaysUntilExpiry = (endDate: string) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper function to get agreement status
  const getAgreementStatus = (agreement: any) => {
    const daysUntilExpiry = getDaysUntilExpiry(agreement.endDate);
    const notifyDays = parseInt(agreement.notifyPeriodInDays || '0', 10);
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: 'bg-red-50 text-red-600 border-red-200' };
    } else if (daysUntilExpiry <= Math.min(7, notifyDays * 0.3)) {
      return { status: 'urgent', label: 'Urgent', color: 'bg-orange-50 text-orange-600 border-orange-200' };
    } else if (daysUntilExpiry <= Math.min(15, notifyDays * 0.6)) {
      return { status: 'warning', label: 'Warning', color: 'bg-yellow-50 text-yellow-600 border-yellow-200' };
    }
    return { status: 'normal', label: 'Normal', color: 'bg-blue-50 text-blue-600 border-blue-200' };
  };

  if (!canViewNotifications) {
    return null;
  }

  if (loading || sharedLoading) {
    return (
      <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl font-bold">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg">
              <AlertTriangle className="h-5 w-5" />
            </div>
            Agreement Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl font-bold">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg">
              <AlertTriangle className="h-5 w-5" />
            </div>
            Agreement Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const totalNotifications = notifications.reduce((total, notification) => {
    return total + notification.agreementInfo.length; // Already filtered to most recent agreements
  }, 0);

  // Pagination logic
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // Flatten all agreements for pagination (already filtered to most recent agreements)
  const allAgreements = notifications.flatMap(notification => 
    notification.agreementInfo.map(agreement => ({
      ...agreement,
      employee: notification.employee,
      notificationId: notification._id
    }))
  );
  
  const paginatedAgreements = allAgreements.slice(startIndex, endIndex);

  if (totalNotifications === 0) {
    return (
      <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl font-bold">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg">
              <FileText className="h-5 w-5" />
            </div>
            Agreement Notifications
            <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100 border-0">
              All Good
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Urgent Agreements</h3>
            <p className="text-gray-600">All employee agreements are up to date!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
      <CardHeader className="pb-6">
        <CardTitle className="flex items-center gap-3 text-xl font-bold">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg">
            <AlertTriangle className="h-5 w-5" />
          </div>
          Agreement Notifications
          <Badge variant="default" className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-0">
            {totalNotifications} Need Attention
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="space-y-0 max-h-96 overflow-y-auto">
          {paginatedAgreements.map((agreement, index) => {
            const statusInfo = getAgreementStatus(agreement);
            const daysUntilExpiry = getDaysUntilExpiry(agreement.endDate);
            
            return (
              <Link 
                key={`${agreement.notificationId}-${index}`}
                to={`/employee/${agreement.employee._id}`}
                className="block bg-white border-b border-gray-100 hover:bg-gray-50 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Profile Picture */}
                    <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:ring-2 group-hover:ring-blue-100 transition-all duration-200">
                      <User className="h-6 w-6 text-gray-400" />
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-base group-hover:text-blue-600 transition-colors duration-200">
                        {agreement.employee.user.firstName} {agreement.employee.user.lastName}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {agreement.employee.user.employeeId || 'Employee'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side content */}
                  <div className="flex items-center gap-4">
                    {/* Status Badge */}
                    <Badge
                      variant="outline"
                      className={`${statusInfo.color} border-0 text-sm px-3 py-1.5 font-medium`}
                    >
                      {statusInfo.label}
                    </Badge>
                    
                    {/* Time Info */}
                    <div className="text-sm text-gray-600 flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">
                        {daysUntilExpiry < 0 
                          ? `Expired ${Math.abs(daysUntilExpiry)}d ago`
                          : `${daysUntilExpiry}d left`
                        }
                      </span>
                    </div>
                    
                    {/* Arrow Icon */}
                    <div className="text-gray-400 group-hover:text-blue-500 transition-colors duration-200">
                      <ExternalLink className="h-5 w-5" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        
        
      </CardContent>
    </Card>
  );
};

export default AgreementNotifications;
